import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useClients } from '@/features/clients/hooks/useClients';
import {
  useSession, useUpdateSession,
  BODY_ZONES, TATTOO_STYLES,
} from '@/features/sessions/hooks/useSessions';
import { useProducts, useSaveProduct, PRODUCT_CATEGORIES } from '@/features/inventory/hooks/useProducts';
import { useSignedContracts, useTemplates } from '@/shared/hooks/useCloudData';
import { generateContractPdf } from '@/features/contracts/services/generatePdf';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft, Camera, FileText,
  Plus, X, Clock, User, Pencil, Check, AlertTriangle,
  Eye, Download, Search, Zap,
} from 'lucide-react';

const localeMap = { fr, en: enUS, es };


const TABS = ['info', 'contract', 'materials', 'photos'] as const;
type Tab = typeof TABS[number];

const TAB_LABELS = {
  fr: { info: 'Infos', contract: 'Contrat', materials: 'Matériaux', photos: 'Photos' },
  en: { info: 'Info', contract: 'Contract', materials: 'Materials', photos: 'Photos' },
  es: { info: 'Info', contract: 'Contrato', materials: 'Materiales', photos: 'Fotos' },
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    in_progress: { label: 'En cours', color: 'bg-primary/10 text-primary border-primary/20' },
    completed: { label: 'Terminée', color: 'bg-green-500/10 text-green-400 border-green-500/20' },
    cancelled: { label: 'Annulée', color: 'bg-destructive/10 text-destructive border-destructive/20' },
  };
  const s = map[status] ?? map.in_progress;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${s.color}`}>
      {status === 'in_progress' && <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
      {s.label}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs text-muted-foreground shrink-0 w-16">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const locale = localeMap[language];
  const tabLabels = TAB_LABELS[language];

  const { data: session, isLoading } = useSession(id);
  const updateSession = useUpdateSession();
  const qc = useQueryClient();
  const { data: products = [] } = useProducts();

  const { data: stockMovements = [] } = useQuery({
    queryKey: ['session-stock-movements', id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('stock_movements')
        .select('id, product_id, quantity, movement_type, products(name, brand, reference, lot_number, expiration_date)')
        .eq('session_id', id)
        .in('movement_type', ['reserved', 'consumed']);
      return (data ?? []) as Array<{
        id: string; product_id: string; quantity: number; movement_type: string;
        products: { name: string; brand: string; reference: string; lot_number: string; expiration_date: string | null } | null;
      }>;
    },
    enabled: !!id,
  });

  const handleAddStockMovement = async (productId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    await (supabase as any).from('stock_movements').insert({
      owner_id: user!.id, session_id: id, product_id: productId, quantity: 1, movement_type: 'reserved',
    });
    qc.invalidateQueries({ queryKey: ['session-stock-movements', id] });
  };

  const handleCancelMovement = async (movementId: string) => {
    await (supabase as any).from('stock_movements').update({ movement_type: 'cancelled' }).eq('id', movementId);
    qc.invalidateQueries({ queryKey: ['session-stock-movements', id] });
  };
  const saveProduct = useSaveProduct();
  const { data: allContracts = [] } = useSignedContracts();
  const { data: templates = [] } = useTemplates();
  const { clients } = useClients();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<Tab>('info');
  const [editing, setEditing] = useState(false);

  const [infoForm, setInfoForm] = useState({
    project_name: '', notes: '', body_zone_main: '', body_zone_detail: '', style: '', price: '',
  });
  const [productSearch, setProductSearch] = useState('');
  const [productCategory, setProductCategory] = useState('all');
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '', brand: '', reference: '', category: 'needles', lot_number: '', expiration_date: '', stock: '1',
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    const parts = (session.body_zone || '').split(' > ');
    setInfoForm({
      project_name: session.project_name || '',
      notes: session.notes || '',
      body_zone_main: parts[0] || '',
      body_zone_detail: parts[1] || '',
      style: session.style || '',
      price: session.price?.toString() || '',
    });
  }, [session?.id]);


  const handleSaveInfo = async () => {
    if (!session?.id) return;
    const body_zone = infoForm.body_zone_main
      ? infoForm.body_zone_detail ? `${infoForm.body_zone_main} > ${infoForm.body_zone_detail}` : infoForm.body_zone_main
      : null;
    await updateSession.mutateAsync({
      id: id!, project_name: infoForm.project_name, notes: infoForm.notes,
      body_zone, style: infoForm.style || null,
      price: infoForm.price ? parseFloat(infoForm.price) : null,
    } as any);
    setEditing(false);
    toast({ title: 'Sauvegardé ✓' });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !session || !user) return;
    const path = `${user.id}/sessions/${session.id}/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from('session-photos').upload(path, file);
    if (error) { toast({ title: 'Erreur upload', variant: 'destructive' }); return; }
    if (session.client_id) {
      await supabase.from('client_photos').insert({ client_id: session.client_id, owner_id: user.id, session_id: session.id, storage_path: path, type: 'tattoo' });
    }
    const newPhotos = [...(session.photos || []), path];
    await updateSession.mutateAsync({ id: session.id, photos: newPhotos } as any);
    toast({ title: 'Photo ajoutée ✓' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddNewProduct = async () => {
    if (!newProduct.name) return;
    await saveProduct.mutateAsync({
      name: newProduct.name, brand: newProduct.brand, reference: newProduct.reference,
      category: newProduct.category, lot_number: newProduct.lot_number,
      expiration_date: newProduct.expiration_date || null, stock: parseInt(newProduct.stock) || 1,
    });
    toast({ title: `${newProduct.name} ajouté à l'inventaire ✓` });
    setShowNewProductForm(false);
    setNewProduct({ name: '', brand: '', reference: '', category: 'needles', lot_number: '', expiration_date: '', stock: '1' });
  };

  const closePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }, [previewUrl]);

  const handlePreviewContract = (contractId: string) => {
    const contract = allContracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    const blob = generateContractPdf(contract, template, 'blob') as Blob;
    if (blob) { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(URL.createObjectURL(blob)); }
  };

  const filteredProducts = products.filter(p => {
    const q = productSearch.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q) || p.reference.toLowerCase().includes(q);
    const matchCat = productCategory === 'all' || p.category === productCategory;
    return matchSearch && matchCat;
  });

  const [photoUrls, setPhotoUrls] = useState<{ path: string; url: string }[]>([]);

  // Resolve signed URLs for session photos (private bucket)
  useEffect(() => {
    if (!session?.photos?.length) { setPhotoUrls([]); return; }
    const resolve = async () => {
      const resolved = await Promise.all(
        session.photos.map(async path => {
          if (path.startsWith('http')) return { path, url: path };
          const { data } = await supabase.storage.from('session-photos').createSignedUrl(path, 3600);
          return { path, url: data?.signedUrl || '' };
        })
      );
      setPhotoUrls(resolved);
    };
    resolve();
  }, [session?.photos?.join(',')]);

  const materialsList = stockMovements;
  const clientContracts = session?.client_id ? allContracts.filter(c => (c as any).clientId === session.client_id) : [];
  const linkedContract = allContracts.find(c => c.id === session?.contract_id);

  if (isLoading || !session) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-10 w-48 bg-secondary/50" />
        <Skeleton className="h-32 rounded-2xl bg-secondary/50" />
      </div>
    );
  }

  const client = session.client_id ? clients.find(c => c.id === session.client_id) : null;

  return (
    <div className="pb-10">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/40 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon"
            onClick={() => session.client_id ? navigate(`/clients/${session.client_id}?tab=sessions`) : navigate('/sessions')}
            className="h-9 w-9 rounded-xl bg-secondary/60 hover:bg-secondary shrink-0">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground truncate">{session.client_name || '—'}</span>
              {session.project_name && <span className="text-xs text-muted-foreground truncate">· {session.project_name}</span>}
              <StatusBadge status={session.status} />
            </div>
          </div>
          {session.elapsed_seconds > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-secondary text-muted-foreground font-mono text-sm shrink-0">
              <Clock size={13} />
              {String(Math.floor(session.elapsed_seconds / 3600)).padStart(2,'0')}:{String(Math.floor((session.elapsed_seconds % 3600) / 60)).padStart(2,'0')}:{String(session.elapsed_seconds % 60).padStart(2,'0')}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-5 space-y-5">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold text-xl shrink-0">
            {session.client_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-lg text-foreground">{session.client_name}</p>
            {session.project_name && <p className="text-sm text-muted-foreground">{session.project_name}</p>}
            <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock size={11} className="text-primary/50" />{format(parseISO(session.started_at), 'dd MMM yyyy HH:mm', { locale })}</span>
              {session.ended_at && <span>→ {format(parseISO(session.ended_at), 'HH:mm', { locale })}</span>}
              {session.body_zone && <span className="flex items-center gap-1"><Zap size={11} className="text-primary/50" />{session.body_zone}</span>}
              {session.style && <span>· {session.style}</span>}
              {session.price && <span>· {session.price} €</span>}
            </div>
          </div>
          <div className="hidden sm:flex gap-4 shrink-0 text-center">
            <div><p className="text-xl font-bold text-primary">{stockMovements.length}</p><p className="text-xs text-muted-foreground">Matériaux</p></div>
            <div className="w-px bg-border/50" />
            <div><p className="text-xl font-bold text-primary">{(session.photos || []).length}</p><p className="text-xs text-muted-foreground">Photos</p></div>
          </div>
        </motion.div>

        {/* Tab pills */}
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 shrink-0
                ${activeTab === tab ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
              {tabLabels[tab]}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.15 }}>

            {/* INFO */}
            {activeTab === 'info' && (
              <div className="space-y-4">
                {!editing ? (
                  <>
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5 border-border/50">
                        <Pencil size={13} /> Modifier
                      </Button>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="glass-card p-5 space-y-3">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5"><User size={12} /> Client</h3>
                        <p className="text-sm font-medium text-foreground">{session.client_name}</p>
                        {client?.email && <p className="text-xs text-muted-foreground">{client.email}</p>}
                        {client?.phone && <p className="text-xs text-muted-foreground">{client.phone}</p>}
                      </div>
                      <div className="glass-card p-5 space-y-3">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tatouage</h3>
                        {session.body_zone && <InfoRow label="Zone" value={session.body_zone} />}
                        {session.style && <InfoRow label="Style" value={session.style} />}
                        {session.price != null && <InfoRow label="Prix" value={`${session.price} €`} />}
                      </div>
                      {session.notes && (
                        <div className="glass-card p-5 sm:col-span-2 space-y-2">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</h3>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{session.notes}</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="glass-card p-6 space-y-4">
                    <h3 className="font-semibold">Modifier la session</h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Projet / Description</Label>
                        <Input value={infoForm.project_name} onChange={e => setInfoForm(f => ({ ...f, project_name: e.target.value }))} className="bg-secondary/50 border-border/50" placeholder="Manchette dragon..." />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Prix (€)</Label>
                        <Input type="number" value={infoForm.price} onChange={e => setInfoForm(f => ({ ...f, price: e.target.value }))} className="bg-secondary/50 border-border/50" placeholder="150" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Partie du corps</Label>
                        <Select value={infoForm.body_zone_main} onValueChange={v => setInfoForm(f => ({ ...f, body_zone_main: v, body_zone_detail: '' }))}>
                          <SelectTrigger className="bg-secondary/50 border-border/50"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                          <SelectContent>{Object.keys(BODY_ZONES).map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {infoForm.body_zone_main && (
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Zone précise</Label>
                          <Select value={infoForm.body_zone_detail} onValueChange={v => setInfoForm(f => ({ ...f, body_zone_detail: v }))}>
                            <SelectTrigger className="bg-secondary/50 border-border/50"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                            <SelectContent>{BODY_ZONES[infoForm.body_zone_main]?.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Style</Label>
                        <Select value={infoForm.style} onValueChange={v => setInfoForm(f => ({ ...f, style: v }))}>
                          <SelectTrigger className="bg-secondary/50 border-border/50"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                          <SelectContent>{TATTOO_STYLES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Notes</Label>
                        <Textarea value={infoForm.notes} onChange={e => setInfoForm(f => ({ ...f, notes: e.target.value }))} className="bg-secondary/50 border-border/50 resize-none" rows={3} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setEditing(false)} className="border-border/50">Annuler</Button>
                      <Button onClick={handleSaveInfo} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Check size={14} /> Enregistrer</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CONTRACT */}
            {activeTab === 'contract' && (
              <div className="space-y-4">
                {linkedContract ? (
                  <div className="glass-card p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><FileText size={16} className="text-primary" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{linkedContract.templateName}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Signé le {format(new Date(linkedContract.signedAt), 'dd MMM yyyy', { locale })}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreviewContract(linkedContract.id)}><Eye size={14} /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { const t = templates.find(t => t.id === linkedContract.templateId); generateContractPdf(linkedContract, t, 'save'); }}><Download size={14} /></Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Aucun contrat lié à cette session.</p>
                    {clientContracts.length > 0 && (
                      <div className="glass-card p-4 space-y-3">
                        <h4 className="text-sm font-semibold">Lier un contrat existant</h4>
                        <div className="space-y-2">
                          {clientContracts.map(c => (
                            <div key={c.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/40">
                              <div>
                                <p className="text-sm font-medium">{c.templateName}</p>
                                <p className="text-xs text-muted-foreground">{format(new Date(c.signedAt), 'dd MMM yyyy', { locale })}</p>
                              </div>
                              <Button size="sm" variant="outline" className="border-border/50 h-7 text-xs"
                                onClick={() => updateSession.mutateAsync({ id: session.id, contract_id: c.id } as any)}>
                                Lier
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="glass-card p-4 space-y-2">
                      <h4 className="text-sm font-semibold">Créer un nouveau contrat</h4>
                      <p className="text-xs text-muted-foreground">Choisissez un modèle et faites signer le client maintenant.</p>
                      <Button size="sm" onClick={() => navigate(`/templates?client_id=${session.client_id}&session_id=${session.id}`)}
                        className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Plus size={13} /> Choisir un modèle
                      </Button>
                    </div>
                    <div className="glass-card p-4 opacity-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold">Envoyer un lien à distance</h4>
                          <p className="text-xs text-muted-foreground">Le client remplit le contrat depuis chez lui.</p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground border border-border/50">Bientôt</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* MATERIALS */}
            {activeTab === 'materials' && (
              <div className="space-y-4">
                {materialsList.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">Matériel utilisé ({materialsList.length})</h3>
                    {materialsList.map(m => {
                      const isExpired = m.products?.expiration_date && new Date(m.products.expiration_date) < new Date();
                      return (
                        <div key={m.id} className="glass-card p-3 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-foreground truncate">{m.products?.name || 'Produit inconnu'}</p>
                              {isExpired && <AlertTriangle size={12} className="text-destructive shrink-0" />}
                              {m.movement_type === 'consumed' && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/10 text-green-500 font-medium">Consommé</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {[m.products?.brand, m.products?.reference && `· ${m.products.reference}`, m.products?.lot_number && `· Lot: ${m.products.lot_number}`, m.products?.expiration_date && `· Exp: ${format(new Date(m.products.expiration_date), 'MM/yyyy')}`, `· ×${m.quantity}`].filter(Boolean).join(' ')}
                            </p>
                          </div>
                          {m.movement_type === 'reserved' && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={() => handleCancelMovement(m.id)}>
                              <X size={13} />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="glass-card p-4 space-y-3">
                  <h3 className="text-sm font-semibold">Ajouter depuis l'inventaire</h3>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Rechercher..." className="pl-8 bg-secondary/50 border-border/50 h-9 text-sm" />
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {['all', 'needles', 'inks', 'consumables', 'equipment', 'protection', 'other'].map(cat => (
                      <button key={cat} onClick={() => setProductCategory(cat)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${productCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}>
                        {cat === 'all' ? 'Tous' : PRODUCT_CATEGORIES.find(c => c.value === cat)?.label || cat}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">Aucun produit trouvé</p>
                    ) : filteredProducts.map(p => {
                      const isExpired = p.expiration_date && new Date(p.expiration_date) < new Date();
                      return (
                        <div key={p.id} className="flex items-center justify-between p-2.5 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                              {isExpired && <AlertTriangle size={11} className="text-destructive shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground">{p.brand} {p.reference && `· ${p.reference}`} · Stock: {p.stock}</p>
                          </div>
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-border/50 ml-2 shrink-0" disabled={p.stock <= 0}
                            onClick={() => handleAddStockMovement(p.id)}>
                            <Plus size={13} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="glass-card p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Nouveau produit</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowNewProductForm(!showNewProductForm)} className="h-7 text-xs gap-1 text-muted-foreground">
                      <Plus size={12} /> {showNewProductForm ? 'Annuler' : 'Ajouter'}
                    </Button>
                  </div>
                  {showNewProductForm && (
                    <div className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Nom *</Label><Input value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} className="bg-secondary/50 border-border/50 h-8 text-sm" placeholder="Ex: KWADRON 3RL" /></div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Catégorie</Label>
                          <Select value={newProduct.category} onValueChange={v => setNewProduct(p => ({ ...p, category: v }))}>
                            <SelectTrigger className="bg-secondary/50 border-border/50 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{PRODUCT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Marque</Label><Input value={newProduct.brand} onChange={e => setNewProduct(p => ({ ...p, brand: e.target.value }))} className="bg-secondary/50 border-border/50 h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Référence</Label><Input value={newProduct.reference} onChange={e => setNewProduct(p => ({ ...p, reference: e.target.value }))} className="bg-secondary/50 border-border/50 h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">N° de lot</Label><Input value={newProduct.lot_number} onChange={e => setNewProduct(p => ({ ...p, lot_number: e.target.value }))} className="bg-secondary/50 border-border/50 h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Date d'expiration</Label><Input type="date" value={newProduct.expiration_date} onChange={e => setNewProduct(p => ({ ...p, expiration_date: e.target.value }))} className="bg-secondary/50 border-border/50 h-8 text-sm" /></div>
                      </div>
                      <Button size="sm" onClick={handleAddNewProduct} disabled={!newProduct.name || saveProduct.isPending} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
                        <Plus size={13} /> Créer et ajouter à la session
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PHOTOS */}
            {activeTab === 'photos' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => fileInputRef.current?.click()} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                    <Camera size={13} /> Ajouter une photo
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                </div>
                {photoUrls.length === 0 ? (
                  <div className="glass-card p-12 text-center text-muted-foreground space-y-2">
                    <Camera size={40} className="mx-auto opacity-20" />
                    <p className="text-sm">Aucune photo pour cette session</p>
                    <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} className="gap-1.5 mt-2"><Camera size={13} /> Prendre une photo</Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photoUrls.map(({ path, url }, i) => (
                      <motion.div key={path} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                        className="relative group aspect-square rounded-2xl overflow-hidden bg-secondary/50 border border-border/40">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-2">
                          <button onClick={async () => { const newPhotos = session.photos.filter(p => p !== path); await updateSession.mutateAsync({ id: session.id, photos: newPhotos } as any); }}
                            className="w-7 h-7 rounded-full bg-destructive flex items-center justify-center text-white"><X size={12} /></button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* PDF Preview */}
      <AnimatePresence>
        {previewUrl && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-background flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
              <h2 className="font-semibold">Aperçu contrat</h2>
              <Button variant="ghost" size="icon" onClick={closePreview} className="h-9 w-9 rounded-xl bg-secondary"><X size={18} /></Button>
            </div>
            <iframe src={previewUrl} className="flex-1 w-full" title="PDF Preview" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
